import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Image as ImageIcon, ExternalLink, RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";
import { compressImage } from "@/components/app/PhotoUpload";

const EMPTY = {
  hero_badge: "",
  hero_title: "",
  hero_highlight: "",
  hero_subtitle: "",
  hero_image: "",
  physical_image: "",
  online_image: "",
  progress_image: "",
  price_physical: "",
  price_online: "",
  physical_points: "",
  online_points: "",
  step1_title: "",
  step1_desc: "",
  step2_title: "",
  step2_desc: "",
  step3_title: "",
  step3_desc: "",
  progress_title: "",
  progress_text: "",
  cta_title: "",
  cta_text: "",
  contact_phone: "",
  contact_email: "",
  contact_address: "",
};

function ImageField({ label, value, onChange, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(file, 1200, 0.8);
      onChange(dataUrl);
      toast.success("Gambar sedia — tekan Simpan untuk terbitkan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-start gap-3">
        <div className="grid h-20 w-28 shrink-0 place-items-center overflow-hidden rounded-xl border border-border/70 bg-muted/40">
          {value ? (
            <img src={value} alt={label} className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <Input
            className="h-10"
            placeholder="https://… (URL gambar) atau muat naik →"
            value={value && value.startsWith("data:") ? "" : value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-upload`}>
              {busy ? "…" : "Muat naik gambar"}
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" className="rounded-full text-destructive" onClick={() => onChange("")}>
                Buang
              </Button>
            )}
          </div>
          {value && value.startsWith("data:") && (
            <p className="text-xs text-muted-foreground">Gambar dimuat naik (disimpan bila tekan Simpan)</p>
          )}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-file`} />
        </div>
      </div>
    </div>
  );
}

export default function LandingEditorPage() {
  const { lang } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: typeof e === "string" ? e : e.target.value }));
  const setVal = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  useEffect(() => {
    api.get("/content/landing").then((r) => {
      const cnt = r.data.content || {};
      const steps = cnt.steps || [];
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, cnt[k] || ""])),
        physical_points: (cnt.physical_points || []).join("\n"),
        online_points: (cnt.online_points || []).join("\n"),
        step1_title: steps[0]?.title || "",
        step1_desc: steps[0]?.desc || "",
        step2_title: steps[1]?.title || "",
        step2_desc: steps[1]?.desc || "",
        step3_title: steps[2]?.title || "",
        step3_desc: steps[2]?.desc || "",
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
      const payload = {
        hero_badge: form.hero_badge || null,
        hero_title: form.hero_title || null,
        hero_highlight: form.hero_highlight || null,
        hero_subtitle: form.hero_subtitle || null,
        hero_image: form.hero_image || null,
        physical_image: form.physical_image || null,
        online_image: form.online_image || null,
        progress_image: form.progress_image || null,
        price_physical: form.price_physical || null,
        price_online: form.price_online || null,
        physical_points: toList(form.physical_points),
        online_points: toList(form.online_points),
        steps: [
          { title: form.step1_title, desc: form.step1_desc },
          { title: form.step2_title, desc: form.step2_desc },
          { title: form.step3_title, desc: form.step3_desc },
        ],
        progress_title: form.progress_title || null,
        progress_text: form.progress_text || null,
        cta_title: form.cta_title || null,
        cta_text: form.cta_text || null,
        contact_phone: form.contact_phone || null,
        contact_email: form.contact_email || null,
        contact_address: form.contact_address || null,
      };
      await api.put("/content/landing", payload);
      toast.success(lang === "ms" ? "Landing page dikemas kini!" : "Landing page updated!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Kembalikan semua kepada kandungan asal (default)?")) return;
    setBusy(true);
    try {
      await api.put("/content/landing", {
        ...Object.fromEntries(Object.keys(EMPTY).filter((k) => !k.startsWith("step")).map((k) => [k, null])),
        physical_points: [],
        online_points: [],
        steps: [],
      });
      setForm(EMPTY);
      toast.success("Dikembalikan kepada default");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="landing-editor">
      <PageHeader
        eyebrow="Admin"
        title="Landing Page"
        subtitle="Edit info dan gambar landing page. Kosongkan medan untuk guna teks asal (default)."
        action={
        <div className="flex flex-wrap gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer">
            <Button className="gap-2 rounded-full border-2 border-blue-300/40 bg-white/10 font-display text-xs font-extrabold uppercase tracking-wide text-blue-100 backdrop-blur transition-colors hover:bg-white/20" data-testid="preview-landing">
              <ExternalLink className="h-4 w-4" /> Pratonton
            </Button>
          </a>
          <Button onClick={reset} className="gap-2 rounded-full border-2 border-red-300/40 bg-white/10 font-display text-xs font-extrabold uppercase tracking-wide text-red-200 backdrop-blur transition-colors hover:bg-red-500/20" data-testid="reset-landing">
            <RotateCcw className="h-4 w-4" /> Reset
          </Button>
          <Button onClick={save} disabled={busy} className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-xs font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="save-landing">
            <Save className="h-4 w-4" /> {busy ? "Menyimpan…" : "Simpan"}
          </Button>
        </div>
        }
      />

      {/* Hero */}
      <Card className="rounded-2xl">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-bold">Bahagian Hero (atas)</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>Lencana kecil</Label><Input className="h-11" placeholder="Program membaca kanak-kanak 4–9 tahun" value={form.hero_badge} onChange={set("hero_badge")} data-testid="ed-hero-badge" /></div>
            <div><Label>Harga tidak perlu diisi di sini</Label><p className="pt-2 text-xs text-muted-foreground">Harga pakej diedit di bahagian Pakej di bawah.</p></div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>Tajuk utama</Label><Input className="h-11" placeholder="Bantu anak anda" value={form.hero_title} onChange={set("hero_title")} data-testid="ed-hero-title" /></div>
            <div><Label>Tajuk berwarna hijau (sambungan)</Label><Input className="h-11" placeholder="lancar membaca dalam 3 tahap" value={form.hero_highlight} onChange={set("hero_highlight")} data-testid="ed-hero-highlight" /></div>
          </div>
          <div><Label>Perenggan pengenalan</Label><Textarea rows={3} placeholder="Kelas Program Membaca PMM menggunakan kaedah suku kata…" value={form.hero_subtitle} onChange={set("hero_subtitle")} data-testid="ed-hero-subtitle" /></div>
          <ImageField label="Gambar hero" value={form.hero_image} onChange={setVal("hero_image")} testId="ed-hero-image" />
        </CardContent>
      </Card>

      {/* 3 Tahap */}
      <Card className="rounded-2xl">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-bold">3 Tahap Membaca</h2>
          {[1, 2, 3].map((n) => (
            <div key={n} className="grid gap-4 rounded-xl border border-border/60 p-4 md:grid-cols-2">
              <div><Label>Tajuk Tahap {n}</Label><Input className="h-11" placeholder={`Tahap ${n} — …`} value={form[`step${n}_title`]} onChange={set(`step${n}_title`)} data-testid={`ed-step${n}-title`} /></div>
              <div><Label>Keterangan Tahap {n}</Label><Textarea rows={2} value={form[`step${n}_desc`]} onChange={set(`step${n}_desc`)} data-testid={`ed-step${n}-desc`} /></div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Packages */}
      <Card className="rounded-2xl">
        <CardContent className="space-y-6 p-6">
          <h2 className="font-display text-lg font-bold">Pakej Kelas</h2>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4 rounded-xl border border-border/60 p-4">
              <h3 className="font-semibold">Kelas Fizikal</h3>
              <div><Label>Harga (cth: RM150)</Label><Input className="h-11" placeholder="RM150" value={form.price_physical} onChange={set("price_physical")} data-testid="ed-price-physical" /></div>
              <div><Label>Senarai kelebihan (satu per baris)</Label><Textarea rows={4} placeholder={"Kelas bersemuka di pusat PMM\nKumpulan kecil & fokus"} value={form.physical_points} onChange={set("physical_points")} data-testid="ed-physical-points" /></div>
              <ImageField label="Gambar kelas fizikal" value={form.physical_image} onChange={setVal("physical_image")} testId="ed-physical-image" />
            </div>
            <div className="space-y-4 rounded-xl border border-border/60 p-4">
              <h3 className="font-semibold">Kelas Online</h3>
              <div><Label>Harga (cth: RM120)</Label><Input className="h-11" placeholder="RM120" value={form.price_online} onChange={set("price_online")} data-testid="ed-price-online" /></div>
              <div><Label>Senarai kelebihan (satu per baris)</Label><Textarea rows={4} placeholder={"Kelas melalui Google Meet\nJadual fleksibel waktu malam"} value={form.online_points} onChange={set("online_points")} data-testid="ed-online-points" /></div>
              <ImageField label="Gambar kelas online" value={form.online_image} onChange={setVal("online_image")} testId="ed-online-image" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress section */}
      <Card className="rounded-2xl">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-bold">Bahagian Perkembangan Anak (hijau)</h2>
          <div><Label>Tajuk</Label><Input className="h-11" placeholder="Lihat sendiri perkembangan anak anda" value={form.progress_title} onChange={set("progress_title")} data-testid="ed-progress-title" /></div>
          <div><Label>Perenggan</Label><Textarea rows={3} value={form.progress_text} onChange={set("progress_text")} data-testid="ed-progress-text" /></div>
          <ImageField label="Gambar bahagian ini" value={form.progress_image} onChange={setVal("progress_image")} testId="ed-progress-image" />
        </CardContent>
      </Card>

      {/* CTA + contact */}
      <Card className="rounded-2xl">
        <CardContent className="space-y-4 p-6">
          <h2 className="font-display text-lg font-bold">Seruan Daftar (CTA) & Maklumat Hubungan</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>Tajuk CTA</Label><Input className="h-11" placeholder="Sedia untuk mulakan…" value={form.cta_title} onChange={set("cta_title")} data-testid="ed-cta-title" /></div>
            <div><Label>Teks CTA</Label><Input className="h-11" placeholder="Daftar akaun ibu bapa secara percuma…" value={form.cta_text} onChange={set("cta_text")} data-testid="ed-cta-text" /></div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div><Label>No. telefon</Label><Input className="h-11" placeholder="+60 12-345 6789" value={form.contact_phone} onChange={set("contact_phone")} data-testid="ed-contact-phone" /></div>
            <div><Label>E-mel</Label><Input className="h-11" placeholder="admin@membacapmm.com" value={form.contact_email} onChange={set("contact_email")} data-testid="ed-contact-email" /></div>
            <div><Label>Alamat</Label><Input className="h-11" placeholder="Kuala Lumpur, Malaysia" value={form.contact_address} onChange={set("contact_address")} data-testid="ed-contact-address" /></div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="h-11 rounded-full gap-2 px-8" data-testid="save-landing-bottom">
          <Save className="h-4 w-4" /> {busy ? "Menyimpan…" : "Simpan semua"}
        </Button>
      </div>
    </div>
  );
}
